<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import StorageCleanupsAPI from 'dashboard/api/storageCleanups';
import UsagesAPI from 'dashboard/api/usages';

const { t } = useI18n();
const { accountId } = useAccount();

const quotaInfo = ref({ total: 0, used: 0, balance: 0, usage_percentage: 0 });
const isLoadingQuota = ref(false);
const startDate = ref('');
const endDate = ref('');
const previewData = ref(null);
const isLoading = ref(false);
const isDeleting = ref(false);
const showConfirmDialog = ref(false);
const barTooltipLeft = ref(0);
const currentPage = ref(1);
const totalPages = ref(1);
const totalCount = ref(0);

const onBarMouseMove = e => {
  const rect = e.currentTarget.getBoundingClientRect();
  barTooltipLeft.value = e.clientX - rect.left;
};

const quickSelectDays = [
  { days: 30, label: '30天前' },
  { days: 90, label: '90天前' },
  { days: 180, label: '180天前' },
  { days: 365, label: '1年前' },
];

const formatBytes = bytes => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / k ** i).toFixed(2)} ${sizes[i]}`;
};

const formatDateISO = date => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const fetchQuotaInfo = async () => {
  isLoadingQuota.value = true;
  try {
    const res = await UsagesAPI.getUsage();
    const s = res.data.storage || {};
    quotaInfo.value = {
      total: s.total || 0,
      used: s.consumed || 0,
      balance: (s.total || 0) - (s.consumed || 0),
      usage_percentage: s.percentage || 0,
    };
  } catch (err) { console.error('fetchQuotaInfo error:', err); } finally { isLoadingQuota.value = false; }
};

const handleQuickSelect = days => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  startDate.value = formatDateISO(start);
  endDate.value = formatDateISO(end);
};

const fetchPreview = async (page = 1) => {
  if (!startDate.value || !endDate.value) return;
  isLoading.value = true;
  try {
    const s = new Date(startDate.value);
    const e = new Date(endDate.value);
    e.setHours(23, 59, 59, 999);
    const res = await StorageCleanupsAPI.preview({
      start_date: s.toISOString(),
      end_date: e.toISOString(),
      page,
      per_page: 20
    });
    previewData.value = res.data;
    currentPage.value = res.data.meta?.current_page || 1;
    totalPages.value = res.data.meta?.total_pages || 1;
    totalCount.value = res.data.meta?.total_count || 0;
  } catch (err) {
    useAlert(err.response?.data?.error || t('STORAGE_CLEANUP.ERROR.FETCH_FAILED'));
  } finally { isLoading.value = false; }
};

const clearFilters = () => { currentPage.value = 1; handleQuickSelect(30); };
const hasFilters = computed(() => !!startDate.value && !!endDate.value);

const handlePageChange = page => { fetchPreview(page); };

const fileTypeLabel = type => {
  if (type === 'conversation') return t('STORAGE_CLEANUP.TYPE.CONVERSATION');
  if (type === 'pilot_document') {
    return t('STORAGE_CLEANUP.TYPE.PILOT_DOCUMENT');
  }
  if (type === 'faq_image') return t('STORAGE_CLEANUP.TYPE.FAQ_IMAGE');
  return t('STORAGE_CLEANUP.TYPE.UNKNOWN');
};

const fileTypeClass = type => {
  if (type === 'conversation') return 'bg-blue-100 text-blue-700';
  if (type === 'faq_image') return 'bg-emerald-100 text-emerald-700';
  return 'bg-purple-100 text-purple-700';
};

const paginationText = computed(() => {
  const start = (currentPage.value - 1) * 20 + 1;
  const end = Math.min(currentPage.value * 20, totalCount.value);
  return `${start}-${end} / ${totalCount.value}`;
});

const handleDelete = async () => {
  isDeleting.value = true;
  try {
    const s = new Date(startDate.value);
    const e = new Date(endDate.value);
    e.setHours(23, 59, 59, 999);
    const res = await StorageCleanupsAPI.cleanup({ start_date: s.toISOString(), end_date: e.toISOString() });
    useAlert(t('STORAGE_CLEANUP.SUCCESS.DELETE_COMPLETE', { count: res.data.deleted_count }));
    showConfirmDialog.value = false;
    previewData.value = null;
    fetchQuotaInfo();
  } catch (err) {
    useAlert(err.response?.data?.error || t('STORAGE_CLEANUP.ERROR.DELETE_FAILED'));
  } finally { isDeleting.value = false; }
};

watch([startDate, endDate], () => { if (startDate.value && endDate.value) fetchPreview(1); });

const barColor = computed(() => {
  const p = quotaInfo.value.usage_percentage;
  if (p >= 90) return '#ef4444';
  if (p >= 70) return '#f59e0b';
  return '#10b981';
});

onMounted(() => { fetchQuotaInfo(); handleQuickSelect(30); });
</script>

<template>
  <SettingsLayout :is-loading="false" :no-records-found="false">
    <template #header>
      <BaseSettingsHeader
        :title="$t('STORAGE_SETTINGS.TITLE')"
        :description="$t('STORAGE_SETTINGS.DESCRIPTION')"
        feature-name="storage_settings"
      >
        <template #actions>
          <Button icon="i-lucide-refresh-cw" :label="$t('BILLING_INVOICES.REFRESH')" :is-loading="isLoadingQuota" @click="fetchQuotaInfo(); fetchPreview(currentPage)" />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="grid gap-6">

        <!-- 统计卡片 -->
        <div class="bg-n-solid-2 rounded-xl border border-n-weak p-6">
          <div class="grid grid-cols-4 divide-x divide-n-weak">
            <div class="px-4 first:pl-0">
              <div class="text-xs text-n-slate-8 mb-1">{{ t('ACCOUNT_STORAGE.TOTAL_QUOTA') }}</div>
              <div class="text-2xl font-semibold text-n-slate-12">{{ formatBytes(quotaInfo.total) }}</div>
            </div>
            <div class="px-4">
              <div class="text-xs text-n-slate-8 mb-1">{{ t('ACCOUNT_STORAGE.CURRENT_USAGE') }}</div>
              <div class="text-2xl font-semibold text-n-slate-12">{{ formatBytes(quotaInfo.used) }}</div>
            </div>
            <div class="px-4">
              <div class="text-xs text-n-slate-8 mb-1">{{ t('ACCOUNT_STORAGE.REMAINING') }}</div>
              <div class="text-2xl font-semibold text-n-slate-12">{{ formatBytes(quotaInfo.balance) }}</div>
            </div>
            <div class="px-4 last:pr-0">
              <div class="text-xs text-n-slate-8 mb-1">{{ t('ACCOUNT_STORAGE.USAGE_PERCENTAGE') }}</div>
              <div class="text-2xl font-semibold text-n-slate-12">{{ quotaInfo.usage_percentage.toFixed(2) }}%</div>
            </div>
          </div>
          <div class="mt-4">
            <div class="w-full bg-slate-100 rounded-full h-3 mb-3 relative progress-wrapper" @mousemove="onBarMouseMove">
              <div class="h-3 rounded-full progress-bar" :style="{ width: Math.min(quotaInfo.usage_percentage, 100) + '%', backgroundColor: barColor }" />
              <div class="progress-tooltip" :style="{ left: barTooltipLeft + 'px' }">
                <div class="progress-tooltip-title">{{ quotaInfo.usage_percentage.toFixed(2) }}% {{ t('SUBSCRIPTION.USAGE.USED') }}</div>
                <div class="progress-tooltip-detail">{{ formatBytes(quotaInfo.used) }} / {{ formatBytes(quotaInfo.total) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 筛选区域 -->
        <div class="bg-n-solid-2 rounded-xl border border-n-weak p-6">
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-n-slate-12 whitespace-nowrap h-[34px] leading-[34px]">{{ t('STORAGE_CLEANUP.START_DATE') }}</label>
              <input v-model="startDate" type="date" style="margin-bottom: 0" class="px-3 py-2 border border-n-weak rounded-lg text-sm h-[34px] focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-n-slate-12 whitespace-nowrap h-[34px] leading-[34px]">{{ t('STORAGE_CLEANUP.END_DATE') }}</label>
              <input v-model="endDate" type="date" style="margin-bottom: 0" class="px-3 py-2 border border-n-weak rounded-lg text-sm h-[34px] focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div class="h-5 w-px bg-n-weak" />
            <div class="flex items-center gap-2">
              <button v-for="item in quickSelectDays" :key="item.days" class="px-3 py-2 text-xs font-medium rounded-lg border border-n-weak hover:bg-n-weak transition-colors" @click="handleQuickSelect(item.days)">{{ item.label }}</button>
            </div>
            <Button variant="ghost" :label="$t('BILLING_INVOICES.FILTER.CLEAR')" :disabled="!hasFilters" @click="clearFilters" />
          </div>
        </div>

        <!-- 文件列表 -->
        <div class="bg-n-solid-2 rounded-xl border border-n-weak p-6">
          <div v-if="isLoading" class="py-12 text-center text-n-slate-11">{{ t('STORAGE_CLEANUP.LOADING') }}</div>
          <div v-else-if="previewData">
            <div class="flex items-center justify-between mb-4">
              <div class="text-sm text-n-slate-11">
                {{ t('STORAGE_CLEANUP.FILES_COUNT') }}: {{ previewData.file_count }}
                <span class="ml-2">{{ t('STORAGE_CLEANUP.TOTAL_SIZE') }}: {{ previewData.total_size_gb }} GB</span>
              </div>
              <Button variant="ghost" icon="i-lucide-trash-2" :label="t('STORAGE_CLEANUP.DELETE_FILES')" :disabled="previewData.file_count === 0" class="!text-red-500 hover:!text-red-600" @click="showConfirmDialog = true" />
            </div>
            <table class="w-full divide-y divide-n-weak">
              <thead>
                <tr>
                  <th class="py-3 text-left text-sm font-semibold text-n-slate-11">{{ t('STORAGE_CLEANUP.TABLE.FILE_NAME') }}</th>
                  <th class="py-3 text-left text-sm font-semibold text-n-slate-11">{{ t('STORAGE_CLEANUP.TABLE.CONTENT_TYPE') }}</th>
                  <th class="py-3 text-left text-sm font-semibold text-n-slate-11">{{ t('STORAGE_CLEANUP.TABLE.TYPE') }}</th>
                  <th class="py-3 text-right text-sm font-semibold text-n-slate-11">{{ t('STORAGE_CLEANUP.TABLE.SIZE') }}</th>
                  <th class="py-3 text-right text-sm font-semibold text-n-slate-11">{{ t('STORAGE_CLEANUP.TABLE.CREATED_AT') }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="file in previewData.files" :key="`${file.type}-${file.id}`" class="text-n-slate-12">
                  <td class="py-4 text-sm"><span class="truncate block max-w-xs">{{ file.file_name || t('STORAGE_CLEANUP.TYPE.NO_FILENAME') }}</span></td>
                  <td class="py-4 text-sm">
                    <span class="inline-flex px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-600 uppercase">{{ file.file_ext || t('STORAGE_CLEANUP.TYPE.UNKNOWN') }}</span>
                  </td>
                  <td class="py-4 text-sm">
                    <span class="inline-flex px-2 py-1 text-xs font-medium rounded-full" :class="fileTypeClass(file.type)">
                      {{ fileTypeLabel(file.type) }}
                    </span>
                  </td>
                  <td class="py-4 text-sm text-right">{{ file.file_size_mb }} MB</td>
                  <td class="py-4 text-sm text-right text-n-slate-11">{{ new Date(file.created_at).toLocaleString('zh-CN') }}</td>
                </tr>
              </tbody>
            </table>
            <!-- 分页 -->
            <div v-if="previewData && previewData.files.length > 0" class="flex items-center justify-between mt-4 pt-4 border-t border-n-weak">
              <span class="text-sm text-n-slate-8">{{ paginationText }}</span>
              <div class="flex items-center gap-2">
                <button
                  class="px-3 py-1.5 text-sm rounded-lg border border-n-weak hover:bg-n-weak transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="currentPage <= 1"
                  @click="handlePageChange(currentPage - 1)"
                >
                  {{ t('STORAGE_CLEANUP.PAGINATION.PREV') }}
                </button>
                <button
                  v-for="p in totalPages"
                  :key="p"
                  class="w-8 h-8 text-sm rounded-lg transition-colors"
                  :class="p === currentPage ? 'bg-n-solid-blue text-white' : 'border border-n-weak hover:bg-n-weak'"
                  @click="handlePageChange(p)"
                >
                  {{ p }}
                </button>
                <button
                  class="px-3 py-1.5 text-sm rounded-lg border border-n-weak hover:bg-n-weak transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="currentPage >= totalPages"
                  @click="handlePageChange(currentPage + 1)"
                >
                  {{ t('STORAGE_CLEANUP.PAGINATION.NEXT') }}
                </button>
              </div>
            </div>
          </div>
          <div v-else class="py-12 text-center text-n-slate-11">{{ t('STORAGE_CLEANUP.SELECT_DATE_RANGE') }}</div>
        </div>

      </div>
    </template>

    <!-- 弹窗放在 default slot 里 -->
    <div v-if="showConfirmDialog" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="bg-n-surface-1 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <h3 class="text-lg font-medium text-n-slate-12 mb-4">{{ t('STORAGE_CLEANUP.CONFIRM_DELETE.TITLE') }}</h3>
        <div class="mb-6">
          <p class="text-n-slate-11 mb-4">{{ t('STORAGE_CLEANUP.CONFIRM_DELETE.MESSAGE') }}</p>
          <div class="bg-n-weak rounded-lg p-4 space-y-2">
            <div class="flex justify-between">
              <span class="text-n-slate-8">{{ t('STORAGE_CLEANUP.FILES_COUNT') }}:</span>
              <span class="font-medium text-n-slate-12">{{ previewData?.file_count || 0 }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-n-slate-8">{{ t('STORAGE_CLEANUP.FREED_SPACE') }}:</span>
              <span class="font-medium text-green-600">{{ previewData?.total_size_gb || 0 }} GB</span>
            </div>
          </div>
          <p class="text-red-500 text-sm mt-4 font-medium">{{ t('STORAGE_CLEANUP.CONFIRM_DELETE.WARNING') }}</p>
        </div>
        <div class="flex justify-end gap-3">
          <Button variant="ghost" :label="t('STORAGE_CLEANUP.CONFIRM_DELETE.CANCEL')" :disabled="isDeleting" @click="showConfirmDialog = false" />
          <Button variant="ghost" :label="isDeleting ? t('STORAGE_CLEANUP.DELETING') : t('STORAGE_CLEANUP.CONFIRM_DELETE.CONFIRM')" :is-loading="isDeleting" class="!text-red-500 hover:!text-red-600" @click="handleDelete" />
        </div>
      </div>
    </div>
  </SettingsLayout>
</template>

<style>
.progress-wrapper {
  cursor: pointer;
  overflow: visible;
  position: relative;
}

.progress-bar {
  transition: filter 0.2s, box-shadow 0.2s, transform 0.2s;
  position: relative;
  overflow: hidden;
  transform-origin: center;
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.25), transparent);
  border-radius: 999px 999px 0 0;
}

.progress-wrapper:hover .progress-bar {
  filter: brightness(1.15) saturate(1.1);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transform: scaleY(2);
}

.progress-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  background: #1e293b;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s, visibility 0.15s;
  pointer-events: none;
  z-index: 10;
}

.progress-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #1e293b;
}

.progress-wrapper:hover .progress-tooltip {
  opacity: 1;
  visibility: visible;
}

.progress-tooltip-title {
  font-weight: 500;
}

.progress-tooltip-detail {
  color: #94a3b8;
  font-size: 11px;
}
</style>
